import { awaitMinSkeletonDelay, byId } from './domUtils';
import { renderEmptyStateMarkup, type EmptyStateIcon } from './emptyStateMarkup';
import { paintHomeNewTopicSkeletons, paintHomeTopicRowSkeletons } from './homeTopicSkeletons';
import { destroyAllHomeTopicCollections, destroyHomeTopicCollection, initializeHomeTopicCollection } from './homeTopicCollectionLayout';
import { clearHomeRecommendedInitialPage, getHomeRecommendedSession, getHomeRecommendedTopics, HOME_RECOMMENDATION_PAGE_SIZE, saveHomeRecommendedSession } from './homeRecommendedTopics';
import { clearListEnterAnimations, clearPagedInfiniteList, isPagedInfiniteListLoading, renderPagedInfiniteList, suspendPagedInfiniteList } from './progressiveList';
import { buildTopicCard, observeAdaptiveCategoryBudget } from './topicCard';
import { applyVisitorCatalogCta, getRuntimePlan } from './visitorCatalogCta';
import { homeReturnStateFromHistory } from './homeReturnState';
import { clearTopicNavigationPreviews, rememberTopicNavigationPreviews } from './topicNavigationPreview';
import type { PublicTopicSummary } from './apiTopicContracts';
import type { PrototypeApiClient } from './apiClient';
import type { Root } from './prototypeTypes';

let hydrationGeneration = 0;
const activeRecommendationLists = new Map<HTMLElement, HTMLElement>();
const activeDiscoveryApis = new WeakMap<object, PrototypeApiClient>();
interface HomeBackToTopArm {
	armed: boolean;
	resetMinimumScrollTop?: number;
}

const homeBackToTopArms = new WeakMap<object, HomeBackToTopArm>();
const homeBackToTopArmsByTab = new WeakMap<HTMLElement, HomeBackToTopArm>();
const HOME_BACK_TO_TOP_REARM_SCROLL_PX = 96;
const HOME_BACK_TO_TOP_RESET_SCROLL_PX = 64;
const HOME_BACK_TO_TOP_DIRECTION_PX = 12;

interface MobileTrendingSession {
	topics: PublicTopicSummary[];
	hasMore: boolean;
	nextCursor: string | null;
	pageCount: number;
}

const mobileTrendingSessions = new WeakMap<object, MobileTrendingSession>();

/**
 * Converts only an in-flight cached For You page into a return reserve before
 * Home validates its Back snapshot. Generic skeletons remain visible loading
 * signals and must still force the normal fallback path.
 */
export function suspendHomeRecommendedForReturn(root: Root): boolean {
	return suspendHomePagedForReturn(root, 'recommended-list');
}

/** Freezes either mobile long feed while Topic owns the visible page. */
export function suspendHomePagedForReturn(root: Root, listId: 'recommended-list' | 'mobileTrendingFeed'): boolean {
	const list = byId(root, listId);
	const region = list ? activeRecommendationLists.get(list) : undefined;
	if (!list || !region || !isPagedInfiniteListLoading(list)) return false;
	clearListEnterAnimations(list);
	suspendPagedInfiniteList(list);
	region.setAttribute('aria-busy', 'false');
	return true;
}

/** Immediately cancels Home discovery requests when Home is no longer current. */
export function deactivateHomeDiscovery(options: { preserveRecommended?: boolean } = {}): void {
	hydrationGeneration += 1;
	destroyAllHomeTopicCollections();
	clearHomeRecommendedInitialPage();
	const retained = new Map<HTMLElement, HTMLElement>();
	for (const [list, region] of activeRecommendationLists) {
		if (options.preserveRecommended && (list.id === 'recommended-list' || list.id === 'mobileTrendingFeed')) {
			delete list.dataset.returnPagerBound;
			clearListEnterAnimations(list);
			suspendPagedInfiniteList(list);
			region.setAttribute('aria-busy', 'false');
			retained.set(list, region);
			continue;
		}
		clearPagedInfiniteList(list);
		region.setAttribute('aria-busy', 'false');
	}
	activeRecommendationLists.clear();
	resetHomeBackToTop();
	for (const [list, region] of retained) activeRecommendationLists.set(list, region);
}

interface HomeDiscoveryHydrationOptions {
	/** Browser Back restoration: only rebuild the cleared For You pager. */
	returnPreserve?: boolean;
	/** A rejected return marker must never revive a cached recommendation page. */
	ignoreReturnSession?: boolean;
}

/** Hydrates the Home Topic Trending and New rails independently. */
export async function hydrateHomeDiscovery(root: Root, api: PrototypeApiClient, options: HomeDiscoveryHydrationOptions = {}): Promise<void> {
	const generation = ++hydrationGeneration;
	activeDiscoveryApis.set(root, api);
	mountHomeBackToTopArmer(root);
	if (!options.returnPreserve) {
		clearTopicNavigationPreviews(root);
		disarmHomeBackToTop(root);
		mobileTrendingSessions.delete(api);
		delete byId(root, 'recommended-list')?.dataset.returnPagerBound;
		delete byId(root, 'mobileTrendingFeed')?.dataset.returnPagerBound;
	}
	if (!options.returnPreserve) resetHomeBackToTop(root);
	// The media query listener must exist before choosing a desktop/mobile fetch
	// branch: a rotation while either first page is pending must schedule the
	// newly visible surface rather than leave its markup at skeleton/empty.
	if (!options.returnPreserve) mountMobileDiscoveryTabs(root);
	if (options.returnPreserve) {
		await hydrateRecommendedGrid(root, generation, api, true);
		return;
	}
	const discoveryTasks = [
		hydrateRecommendedGrid(root, generation, api, options.ignoreReturnSession ? false : undefined),
		hydrateTopicRail(root, generation, 'homeTopicNewestRail', () => api.topicNewest({ limit: 30 }), {
			emptyTitle: 'No new topics yet',
			emptySubtitle: 'Newly published topics will appear here.',
			emptyIcon: 'sparkles',
			errorTitle: "Couldn't load new topics",
		}, 30),
	];
	if (usesMobileDiscoveryLayout(root)) discoveryTasks.push(hydrateMobileTrendingGrid(root, generation, api));
	else discoveryTasks.push(hydrateTopicRail(root, generation, 'homeTopicTrendingRail', () => api.topicTrending({ limit: 10 }), {
		emptyTitle: 'No trending topics yet',
		emptySubtitle: 'Check back soon to see what learners are exploring.',
		emptyIcon: 'flame',
		errorTitle: "Couldn't load trending topics",
	}));
	await Promise.allSettled(discoveryTasks);
	mountMobileDiscoveryTabs(root);
}

/** Rebinds a paused cached For You pager before a stable Back return is shown. */
export function resumeHomeRecommendedGrid(root: Root, api: PrototypeApiClient): boolean {
	const list = byId(root, 'recommended-list');
	const initialPage = getHomeRecommendedSession(api);
	if (!list || !initialPage || list.querySelector('.topic-card-skeleton:not([data-return-reserve])')) return false;
	const generation = ++hydrationGeneration;
	clearListEnterAnimations(list);
	void hydrateRecommendedGrid(root, generation, api, true, true);
	return true;
}

/** Rebinds the retained mobile Trending DOM so Back can continue page three. */
export function resumeMobileTrendingGrid(root: Root, api: PrototypeApiClient): boolean {
	const list = byId(root, 'mobileTrendingFeed');
	const session = mobileTrendingSessions.get(api);
	if (!list || !session || list.querySelector('.topic-card-skeleton:not([data-return-reserve])')) return false;
	if (list.dataset.returnPagerBound === 'true') return true;
	const generation = ++hydrationGeneration;
	clearListEnterAnimations(list);
	list.dataset.returnPagerBound = 'true';
	void hydrateMobileTrendingGrid(root, generation, api, session, true);
	if (session.pageCount > 1) armHomeBackToTop(root);
	return true;
}

/** Rebinds both mobile long feeds in one generation for a stable Topic Back. */
export function resumeMobileHomeFeeds(root: Root, api: PrototypeApiClient): boolean {
	if (!isMobile(root)) return false;
	const recommended = byId(root, 'recommended-list');
	const trending = byId(root, 'mobileTrendingFeed');
	const recommendedSession = getHomeRecommendedSession(api);
	const trendingSession = mobileTrendingSessions.get(api);
	const canResumeRecommended = Boolean(recommended && recommendedSession && !recommended.querySelector('.topic-card-skeleton:not([data-return-reserve])'));
	const canResumeTrending = Boolean(trending && trendingSession && !trending.querySelector('.topic-card-skeleton:not([data-return-reserve])'));
	if (!canResumeRecommended && !canResumeTrending) return false;
	if (recommended?.dataset.returnPagerBound === 'true' && trending?.dataset.returnPagerBound === 'true') return true;
	const generation = ++hydrationGeneration;
	if (canResumeRecommended && recommended && recommendedSession) {
		clearListEnterAnimations(recommended);
		recommended.dataset.returnPagerBound = 'true';
		void hydrateRecommendedGrid(root, generation, api, true, true);
	}
	if (canResumeTrending && trending && trendingSession) {
		clearListEnterAnimations(trending);
		trending.dataset.returnPagerBound = 'true';
		void hydrateMobileTrendingGrid(root, generation, api, trendingSession, true);
	}
	if (hasRetainedMobileSecondPage(api)) armHomeBackToTop(root);
	return true;
}

function hasRetainedMobileSecondPage(api: PrototypeApiClient): boolean {
	return (getHomeRecommendedSession(api)?.pageCount ?? 0) > 1 || (mobileTrendingSessions.get(api)?.pageCount ?? 0) > 1;
}

function hydrateRecommendedGrid(root: Root, generation: number, api: PrototypeApiClient, useReturnSession?: boolean, preserveExistingDom = false, preserveExistingDomImmediate = false): Promise<void> {
	const list = byId(root, 'recommended-list');
	if (!list) return Promise.resolve();
	const region = byId(root, 'homeRecommendedSection') ?? byId(root, 'featuredSection') ?? list;
	const live = byId(root, 'homeRecommendedLive');
	activeRecommendationLists.set(list, region);
	region.setAttribute('aria-busy', 'true');
	const returningToRecommendation = useReturnSession === true || (useReturnSession === undefined
		&& typeof window !== 'undefined' && homeReturnStateFromHistory(window.history.state)?.containerId === 'recommended-list');
	// The page cache exists exclusively for a browser Back restoration. A
	// regular Home visit deliberately starts a fresh recommendation request so
	// this navigation optimization cannot turn into a permanently stale feed.
	const initialPage = returningToRecommendation ? getHomeRecommendedSession(api) : null;
	const seenSlugs = new Set<string>();
	const loadedItems: PublicTopicSummary[] = [];
	let sessionNextCursor: string | null = initialPage?.nextCursor ?? null;
	let sessionHasMore = initialPage?.hasMore === true;
	let sessionPageCount = initialPage?.pageCount ?? 0;
	if (initialPage) {
		for (const topic of initialPage.topics) seenSlugs.add(topic.slug);
		loadedItems.push(...initialPage.topics);
	}
	let uniqueCount = loadedItems.length;
	let totalTopics = initialPage?.totalTopics ?? 0;
	let renderedInitialPage = false;
	let settleInitialLoad!: () => void;
	let initialLoadSettled = false;
	const initialLoad = new Promise<void>((resolve) => { settleInitialLoad = resolve; });

	renderPagedInfiniteList({
		container: list,
		loadPage: async (cursor) => {
			if (isCurrentGeneration(generation)) region.setAttribute('aria-busy', 'true');
			try {
				const response = cursor === null
					? await getHomeRecommendedTopics(api)
					: await api.topicRecommended({ limit: HOME_RECOMMENDATION_PAGE_SIZE, cursor });
				if (!isCurrentGeneration(generation)) return { items: [], hasMore: false, nextCursor: null };
				if (typeof response.totalTopics === 'number') totalTopics = response.totalTopics;
				const page = uniqueRecommendedPage(response.topics ?? [], response.hasMore === true, response.nextCursor ?? null, seenSlugs);
				uniqueCount += page.items.length;
				return page;
			} catch (error) {
				if (cursor === null && isCurrentGeneration(generation)) updateHomeVisitorCatalogCta(root, 0);
				throw error;
			} finally {
				if (isCurrentGeneration(generation)) region.setAttribute('aria-busy', 'false');
				if (cursor === null && !initialLoadSettled) {
					initialLoadSettled = true;
					settleInitialLoad();
				}
			}
		},
		renderItem: buildRecommendedTopicCard,
		skeletonClassName: 'topic-card-skeleton',
		skeletonCount: homeRecommendationSkeletonCount(),
		endElement: buildRecommendedTerminal,
		emptyElement: buildRecommendedEmpty(),
		onPage: (page) => {
			if (!isCurrentGeneration(generation)) return;
			sessionNextCursor = page.nextCursor ?? null;
			sessionHasMore = page.hasMore === true;
			updateHomeVisitorCatalogCta(root, totalTopics || uniqueCount);
			if (renderedInitialPage) {
				sessionPageCount += 1;
				if (live) live.textContent = `${page.items.length} more topic${page.items.length === 1 ? '' : 's'} loaded`;
			} else {
				renderedInitialPage = true;
				if (sessionPageCount === 0) sessionPageCount = 1;
			}
		},
		onItemsChange: (items) => {
			if (!isCurrentGeneration(generation)) return;
			rememberTopicNavigationPreviews(root, items as PublicTopicSummary[]);
			loadedItems.splice(0, loadedItems.length, ...(items as PublicTopicSummary[]));
			saveHomeRecommendedSession(api, { topics: loadedItems, hasMore: sessionHasMore, nextCursor: sessionNextCursor, totalTopics, pageCount: sessionPageCount });
			if (items.length === 0) updateHomeVisitorCatalogCta(root, totalTopics);
		},
		requireUserIntentForNextPage: isMobile(root),
		...(isMobile(root) ? { onNonInitialPageLoad: () => armHomeBackToTop(root) } : {}),
		// A Back restoration already has the cards in memory. It re-registers the
		// retained DOM without replacing or moving the captured viewport.
		initialPage: initialPage ? { items: initialPage.topics, hasMore: initialPage.hasMore, nextCursor: initialPage.nextCursor, preserveExistingDom, preserveExistingDomImmediate } : undefined,
	});
	if (initialPage) {
		region.setAttribute('aria-busy', 'false');
		return Promise.resolve();
	}
	return initialLoad;
}

function showHomeBackToTop(root: Root): void {
	const tab = root.querySelector<HTMLButtonElement>('#tabNav [data-tab="home"]');
	if (!tab || tab.dataset.homeBackToTop === 'true') return;
	tab.dataset.homeBackToTop = 'true';
	tab.dataset.protoClick = 'scrollHomeToTop()';
	tab.setAttribute('aria-label', 'Back to top');
	tab.querySelector('.tab-home-label')!.textContent = 'Top';
	tab.querySelector<SVGElement>('.tab-home-icon')!.setAttribute('hidden', '');
	tab.querySelector<SVGElement>('.tab-rocket-icon')!.removeAttribute('hidden');
}

function resetHomeBackToTop(root?: Root): void {
	const scope = root ?? (typeof document !== 'undefined' ? document : undefined);
	if (!scope || typeof (scope as { querySelector?: unknown }).querySelector !== 'function') return;
	const tab = scope.querySelector<HTMLButtonElement>('#tabNav [data-tab="home"]');
	if (!tab) return;
	delete homeBackToTopArmsByTab.get(tab)?.resetMinimumScrollTop;
	delete tab.dataset.homeTopScrollResetting;
	if (tab.dataset.homeBackToTop !== 'true') return;
	delete tab.dataset.homeBackToTop;
	tab.dataset.protoClick = "showView('home');setActiveTab('home')";
	tab.removeAttribute('aria-label');
	tab.querySelector('.tab-home-label')!.textContent = 'Home';
	tab.querySelector<SVGElement>('.tab-home-icon')!.removeAttribute('hidden');
	tab.querySelector<SVGElement>('.tab-rocket-icon')!.setAttribute('hidden', '');
}

/**
 * A second page means this Home feed is long enough to make returning to its
 * top useful. Keep that fact for the current in-memory session, so exhausting
 * the final page cannot make the dock depend on another network request.
 */
function armHomeBackToTop(root: Root): void {
	const arm = homeBackToTopArms.get(root);
	if (!arm) return;
	arm.armed = true;
	showHomeBackToTop(root);
}

function disarmHomeBackToTop(root: Root): void {
	const arm = homeBackToTopArms.get(root);
	if (arm) arm.armed = false;
	delete arm?.resetMinimumScrollTop;
	const tab = root.querySelector<HTMLButtonElement>('#tabNav [data-tab="home"]');
	if (tab) {
		if (arm) homeBackToTopArmsByTab.set(tab, arm);
		delete tab.dataset.homeTopScrollResetting;
	}
}

function mountHomeBackToTopArmer(root: Root): void {
	if (homeBackToTopArms.has(root)) return;
	const view = root.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
	if (!view) return;
	const arm: HomeBackToTopArm = { armed: false };
	homeBackToTopArms.set(root, arm);
	const homeTab = root.querySelector<HTMLElement>('#tabNav [data-tab="home"]');
	if (homeTab) homeBackToTopArmsByTab.set(homeTab, arm);
	view.addEventListener('scroll', () => {
		if (!arm.armed || !isMobile(root)) return;
		const tab = root.querySelector<HTMLButtonElement>('#tabNav [data-tab="home"]');
		if (!tab || !root.querySelector('#view-home.active')) return;
		homeBackToTopArmsByTab.set(tab, arm);
		const scrollTop = view.scrollY || view.pageYOffset || 0;
		// A smooth Top action emits scroll events while moving upward. Do not
		// re-show Top until it reaches the top, or a meaningful reverse/downward
		// scroll proves the user has taken control after scroll anchoring settles.
		if (tab.dataset.homeTopScrollResetting === 'true') {
			const minimumScrollTop = Math.min(arm.resetMinimumScrollTop ?? scrollTop, scrollTop);
			arm.resetMinimumScrollTop = minimumScrollTop;
			const resumedDownwardScroll = scrollTop >= minimumScrollTop + HOME_BACK_TO_TOP_DIRECTION_PX;
			if (scrollTop > 8 && !resumedDownwardScroll) return;
			delete tab.dataset.homeTopScrollResetting;
			delete arm.resetMinimumScrollTop;
		} else {
			delete arm.resetMinimumScrollTop;
		}
		// Scrolling back near the top without tapping must restore the Home tab:
		// the rocket is only an affordance while the user is deep in the feed.
		// 64/96 hysteresis keeps overscroll around the threshold from flapping.
		if (scrollTop < HOME_BACK_TO_TOP_RESET_SCROLL_PX) {
			resetHomeBackToTop(root);
			return;
		}
		if (scrollTop >= HOME_BACK_TO_TOP_REARM_SCROLL_PX) showHomeBackToTop(root);
	}, { passive: true });
}

function uniqueRecommendedPage(
	topics: PublicTopicSummary[],
	serverHasMore: boolean,
	nextCursor: string | null,
	seenSlugs: Set<string>,
): { items: PublicTopicSummary[]; hasMore: boolean; nextCursor: string | null } {
	const items: PublicTopicSummary[] = [];
	for (const topic of topics) {
		if (!topic.slug || seenSlugs.has(topic.slug)) continue;
		seenSlugs.add(topic.slug);
		items.push(topic);
	}
	const hasMore = items.length > 0 && serverHasMore && nextCursor !== null;
	return { items, hasMore, nextCursor: hasMore ? nextCursor : null };
}

async function hydrateTopicRail(
	root: Root,
	generation: number,
	railId: string,
	load: () => Promise<{ topics?: PublicTopicSummary[] }>,
	copy: { emptyTitle: string; emptySubtitle: string; emptyIcon: Extract<EmptyStateIcon, 'flame' | 'sparkles'>; errorTitle: string },
	maxCards = 10,
): Promise<void> {
	const rail = byId(root, railId);
	if (!rail) return;
	destroyHomeTopicCollection(rail);
	rail.dataset.layoutState = 'loading';
	const mobileNew = railId === 'homeTopicNewestRail' && isMobile(root);
	if (mobileNew) paintHomeNewTopicSkeletons(rail);
	else paintHomeTopicRowSkeletons(rail);
	try {
		const response = await load();
		await awaitMinSkeletonDelay();
		if (!isCurrentGeneration(generation)) return;
		const topics = (response.topics ?? []).slice(0, maxCards);
		rememberTopicNavigationPreviews(root, topics);
		const mobileClassicTrending = railId === 'homeTopicTrendingRail' && isMobile(root) && !usesMobileDiscoveryLayout(root);
		const cards = mobileNew ? topics.map(buildNewTopicCard) : topics.map((topic) => buildHomeTopicCard(topic, mobileClassicTrending));
		rail.replaceChildren(...(topics.length > 0
			? mobileNew ? buildNewTopicGroups(cards) : cards
			: [buildEmpty(copy.emptyTitle, copy.emptySubtitle, copy.emptyIcon)]));
		rail.dataset.layoutState = topics.length > 0 ? 'ready' : 'empty';
		if (topics.length > 0 && !mobileNew) initializeHomeTopicCollection(rail);
	} catch {
		if (!isCurrentGeneration(generation)) return;
		rail.replaceChildren(buildEmpty(copy.errorTitle, 'Check your connection and try again.', 'inbox'));
		rail.dataset.layoutState = 'error';
	}
}

function buildNewTopicGroups(cards: HTMLElement[]): HTMLElement[] {
	const groups: HTMLElement[] = [];
	for (let index = 0; index < cards.length; index += 3) {
		const group = document.createElement('div');
		group.className = 'home-new-library-group';
		group.replaceChildren(...cards.slice(index, index + 3));
		groups.push(group);
	}
	return groups;
}

function buildNewTopicCard(topic: PublicTopicSummary): HTMLElement {
	const card = buildTopicCard({
		slug: topic.slug, title: topic.title, source: topic.source, coverBadge: topic.coverBadge, subtitle: topic.subtitle || topic.category || 'Topic', category: topic.category,
			coverImageUrl: topic.coverImageUrl, coverGradient: topic.coverGradient, classification: topic.classification,
		isFavorite: topic.isFavorite, voteValue: topic.voteValue,
	}, { showSubtitle: true, variant: 'mobile-compact', reactionActions: 'like-save' });
	card.classList.add('home-new-topic-card');
	return card;
}

function isMobile(root: Root): boolean {
	const view = root.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
	return view?.matchMedia?.('(max-width: 640px)').matches ?? (view?.innerWidth ?? 0) <= 640;
}

/**
 * The For you/Trending tab feed is a member-only mobile surface (18d543022):
 * refreshHomeLayout hides #mobileHomeDiscoverySection for guests, so the
 * guest phone layout must keep the classic Trending rail and leave the
 * Explore more topics grid inside #featuredSection.
 */
function usesMobileDiscoveryLayout(root: Root): boolean {
	return isMobile(root) && root.querySelector('#view-home')?.getAttribute('data-auth') !== 'guest';
}

function mountMobileDiscoveryTabs(root: Root): void {
	const host = byId(root, 'mobileHomeDiscoverySection');
	const recommended = byId(root, 'recommended-list');
	const forYouPanel = byId(root, 'mobileForYouPanel');
	const trendingPanel = byId(root, 'mobileTrendingPanel');
	const forYouTab = byId(root, 'mobileForYouTab');
	const trendingTab = byId(root, 'mobileTrendingTab');
	const forYouViewAll = byId(root, 'mobileForYouViewAll');
	const trendingViewAll = byId(root, 'mobileTrendingViewAll');
	if (!host || !recommended || !forYouPanel || !trendingPanel || !forYouTab || !trendingTab) return;
	const view = root.ownerDocument?.defaultView ?? (typeof window !== 'undefined' ? window : null);
	// Bind before either layout branch: the first paint can be desktop and later
	// rotate into mobile (or the reverse) without leaving the hidden surface's
	// stale skeletons as the only visible content.
	if (host.dataset.layoutBound !== 'true') {
		host.dataset.layoutBound = 'true';
		view?.matchMedia?.('(max-width: 640px)').addEventListener('change', () => {
			if (!root.querySelector('#view-home.active')) return;
			const api = activeDiscoveryApis.get(root);
			if (!api) return;
			const generation = ++hydrationGeneration;
			const recommendedSession = getHomeRecommendedSession(api);
			if (recommendedSession) {
				clearListEnterAnimations(recommended);
				void hydrateRecommendedGrid(root, generation, api, true, true, !isMobile(root));
			} else {
				// The prior first page is intentionally invalid after the layout flip;
				// attach the replacement controller to the shared request so a slow
				// desktop/mobile rotation cannot strand the visible feed empty.
				void hydrateRecommendedGrid(root, generation, api, false);
			}
			void hydrateTopicRail(root, generation, 'homeTopicNewestRail', () => api.topicNewest({ limit: 30 }), {
				emptyTitle: 'No new topics yet', emptySubtitle: 'Newly published topics will appear here.', emptyIcon: 'sparkles', errorTitle: "Couldn't load new topics",
			}, 30);
			if (usesMobileDiscoveryLayout(root)) {
				void hydrateMobileTrendingGrid(root, generation, api);
			} else {
				void hydrateTopicRail(root, generation, 'homeTopicTrendingRail', () => api.topicTrending({ limit: 10 }), {
					emptyTitle: 'No trending topics yet', emptySubtitle: 'Check back soon to see what learners are exploring.', emptyIcon: 'flame', errorTitle: "Couldn't load trending topics",
				});
			}
			mountMobileDiscoveryTabs(root);
		});
	}
	if (!usesMobileDiscoveryLayout(root)) {
		const featured = byId(root, 'featuredSection');
		const live = byId(root, 'homeRecommendedLive');
		if (featured && recommended.parentElement !== featured) featured.insertBefore(recommended, live ?? null);
		return;
	}
	if (recommended.parentElement !== forYouPanel) forYouPanel.appendChild(recommended);
	const setActive = (key: 'for-you' | 'trending') => {
		host.dataset.selected = key;
		const forYou = key === 'for-you';
		forYouTab.setAttribute('aria-selected', String(forYou)); forYouTab.tabIndex = forYou ? 0 : -1; forYouPanel.hidden = !forYou;
		trendingTab.setAttribute('aria-selected', String(!forYou)); trendingTab.tabIndex = forYou ? -1 : 0; trendingPanel.hidden = forYou;
		if (forYouViewAll) forYouViewAll.hidden = !forYou;
		if (trendingViewAll) trendingViewAll.hidden = forYou;
	};
	setActive(host.dataset.selected === 'trending' ? 'trending' : 'for-you');
	if (host.dataset.tabsBound === 'true') return;
	host.dataset.tabsBound = 'true';
	forYouTab.addEventListener('click', () => setActive('for-you'));
	trendingTab.addEventListener('click', () => setActive('trending'));
	host.addEventListener('keydown', (event) => {
		if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
		const tabs = [forYouTab, trendingTab];
		const current = tabs.indexOf(event.target as HTMLButtonElement);
		if (current < 0) return;
		event.preventDefault();
		const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
		tabs[next]!.focus(); tabs[next]!.click();
	});
}

function hydrateMobileTrendingGrid(
	root: Root,
	generation: number,
	api: PrototypeApiClient,
	initialSession?: MobileTrendingSession,
	preserveExistingDom = false,
): Promise<void> {
	if (!isMobile(root)) return Promise.resolve();
	const list = byId(root, 'mobileTrendingFeed');
	if (!list) return Promise.resolve();
	if (!initialSession) delete list.dataset.returnPagerBound;
	const region = byId(root, 'mobileTrendingPanel') ?? list;
	activeRecommendationLists.set(list, region);
	let settleInitialLoad!: () => void;
	let initialSettled = false;
	const initialLoad = new Promise<void>((resolve) => { settleInitialLoad = resolve; });
	const seenSlugs = new Set(initialSession?.topics.map((topic) => topic.slug) ?? []);
	let sessionHasMore = initialSession?.hasMore === true;
	let sessionNextCursor = initialSession?.nextCursor ?? null;
	let sessionPageCount = initialSession?.pageCount ?? 0;
	let renderedInitialPage = false;
	renderPagedInfiniteList({
		container: list,
		loadPage: async (cursor) => {
			region.setAttribute('aria-busy', 'true');
			try {
				const response = await api.topicTrending({ limit: HOME_RECOMMENDATION_PAGE_SIZE, cursor });
				if (!isCurrentGeneration(generation)) return { items: [], hasMore: false, nextCursor: null };
				const items = (response.topics ?? []).filter((topic) => Boolean(topic.slug) && !seenSlugs.has(topic.slug));
				for (const topic of items) seenSlugs.add(topic.slug);
				return { items, hasMore: items.length > 0 && (response as { hasMore?: boolean }).hasMore === true, nextCursor: (response as { nextCursor?: string | null }).nextCursor ?? null };
			} catch (error) {
				// Cursors can expire while a mobile feed is in the back stack. Rebuild
				// from page one rather than presenting a retry wired to that stale cursor.
				if (cursor !== null && isStaleCursorError(error) && isCurrentGeneration(generation)) {
					void hydrateMobileTrendingGrid(root, generation, api);
					return { items: [], hasMore: false, nextCursor: null };
				}
				throw error;
			} finally {
				region.setAttribute('aria-busy', 'false');
				if (cursor === null && !initialSettled) { initialSettled = true; settleInitialLoad(); }
			}
		},
		renderItem: buildRecommendedTopicCard,
		skeletonClassName: 'topic-card-skeleton',
		skeletonCount: homeRecommendationSkeletonCount(),
		emptyElement: buildEmpty('No trending topics yet', 'Check back soon to see what learners are exploring.', 'flame'),
		requireUserIntentForNextPage: true,
		onNonInitialPageLoad: () => armHomeBackToTop(root),
		onItemsChange: (items) => {
			if (!isCurrentGeneration(generation)) return;
			rememberTopicNavigationPreviews(root, items as PublicTopicSummary[]);
			mobileTrendingSessions.set(api, { topics: items as PublicTopicSummary[], hasMore: sessionHasMore, nextCursor: sessionNextCursor, pageCount: sessionPageCount });
		},
		onPage: (page) => {
			if (!isCurrentGeneration(generation)) return;
			if (renderedInitialPage) sessionPageCount += 1;
			else {
				renderedInitialPage = true;
				if (sessionPageCount === 0) sessionPageCount = 1;
			}
			sessionHasMore = page.hasMore === true;
			sessionNextCursor = page.nextCursor ?? null;
		},
		initialPage: initialSession ? {
			items: initialSession.topics, hasMore: initialSession.hasMore, nextCursor: initialSession.nextCursor, preserveExistingDom,
		} : undefined,
	});
	return initialSession ? Promise.resolve() : initialLoad;
}

function isStaleCursorError(error: unknown): boolean {
	return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 410;
}

function isCurrentGeneration(generation: number): boolean {
	return hydrationGeneration === generation;
}

function homeRecommendationSkeletonCount(): number {
	return typeof window !== 'undefined' && window.innerWidth <= 640 ? 2 : 4;
}

function buildRecommendedTopicCard(topic: PublicTopicSummary): HTMLElement {
	const card = buildTopicCard(
		{
				slug: topic.slug,
				title: topic.title,
				source: topic.source,
				coverBadge: topic.coverBadge,
			subtitle: topic.subtitle,
			description: topic.description,
			category: topic.category,
			classification: topic.classification,
			coverImageUrl: topic.coverImageUrl,
			coverGradient: topic.coverGradient,
			coverIcon: topic.coverIcon,
			chapterCount: topic.chapterCount ?? topic.engagement?.chapters,
			audioMinutes: topic.audioMinutes ?? topic.minutes ?? topic.engagement?.mediaMinutes,
			subscribers: topic.subscribers,
			openCount: topic.openCount,
			isFavorite: topic.isFavorite,
			voteValue: topic.voteValue,
		},
		{ showSubtitle: true, adaptiveCategories: true, variant: 'mobile-poster' },
	);
	// The poster footer shares its category width with three reaction targets.
	// Observe this exact card once so font settlement and viewport changes can
	// reveal every category that fits before collapsing the remainder into +N.
	observeAdaptiveCategoryBudget(card);
	return card;
}

function buildRecommendedEmpty(): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = renderEmptyStateMarkup({
		icon: 'userRoundCheck',
		title: 'No recommendations yet',
		subtitle: "Explore a few topics and we'll tailor picks to what interests you.",
		extraClass: 'rec-empty-state',
	});
	const empty = template.content.firstElementChild as HTMLElement;
	const browse = document.createElement('button');
	browse.className = 'btn btn-outline btn-sm';
	browse.textContent = 'Browse all topics';
	browse.setAttribute('data-proto-click', "showView('home-browse');setActiveTab('home-browse')");
	empty.appendChild(browse);
	return empty;
}

function buildRecommendedTerminal(): HTMLElement {
	const terminal = document.createElement('button');
	terminal.type = 'button';
	terminal.className = 'btn btn-outline btn-sm';
	terminal.textContent = 'Browse all topics';
	terminal.setAttribute('data-proto-click', "showView('home-browse');setActiveTab('home-browse')");
	return terminal;
}

function updateHomeVisitorCatalogCta(root: Root, totalTopics: number): void {
	if (getRuntimePlan(root) === 'visitor') byId(root, 'homeVisitorCatalogCta')?.style.setProperty('display', 'block');
	applyVisitorCatalogCta(root, 'home', totalTopics);
}

function buildHomeTopicCard(topic: PublicTopicSummary, mobilePoster = false): HTMLElement {
	const card = buildTopicCard(
		{
				slug: topic.slug,
				title: topic.title,
				source: topic.source,
				coverBadge: topic.coverBadge,
			subtitle: topic.subtitle,
			description: topic.description,
			category: topic.category,
			classification: topic.classification,
			coverImageUrl: topic.coverImageUrl,
			coverGradient: topic.coverGradient,
			coverIcon: topic.coverIcon,
			chapterCount: topic.chapterCount ?? topic.engagement?.chapters,
			audioMinutes: topic.audioMinutes ?? topic.minutes ?? topic.engagement?.mediaMinutes,
			openCount: topic.openCount,
			createdAt: topic.createdAt,
			isFavorite: topic.isFavorite,
			voteValue: topic.voteValue,
		},
		{
			showSubtitle: true,
			compact: !mobilePoster,
			...(mobilePoster ? { adaptiveCategories: true, variant: 'mobile-poster' as const } : {}),
		},
	);
	if (mobilePoster) observeAdaptiveCategoryBudget(card);
	return card;
}

function buildEmpty(title: string, subtitle: string, icon: Extract<EmptyStateIcon, 'flame' | 'inbox' | 'sparkles'>): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = renderEmptyStateMarkup({ icon, title, subtitle, extraClass: 'home-topic-empty-state' });
	return template.content.firstElementChild as HTMLElement;
}
